export interface DateTimeObject {
  rawValue: string;
  format: string;
  unixTimestamp: number | null;
  iso8601: string | null;
  humanReadableLocal: string | null;
  humanReadableUTC: string | null;
  timezone: string;
  isValid: boolean;
}

export type DateInputFormat = 
  | 'unix-timestamp'
  | 'unix-timestamp-ms'
  | 'iso8601'
  | 'mm-dd-yyyy'
  | 'dd-mm-yyyy'
  | 'yyyy-mm-dd'
  | 'mm/dd/yyyy'
  | 'dd/mm/yyyy'
  | 'yyyy/mm/dd'
  | 'auto';

export interface DateConverterOptions {
  inputFormat: DateInputFormat;
  timezone: string;
  outputFormats: string[];
}

export class DateParserUtil {
  private options: DateConverterOptions;

  constructor(options: Partial<DateConverterOptions> = {}) {
    this.options = {
      inputFormat: 'auto',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      outputFormats: ['iso8601', 'unix-timestamp', 'local', 'utc'],
      ...options
    };
  }

  parse(input: string, customFormat?: DateInputFormat): DateTimeObject {
    const trimmedInput = input.trim();
    
    if (!trimmedInput) {
      return this.createEmptyResult(input, 'auto');
    }

    const format = customFormat || this.options.inputFormat;
    let date: Date | null = null;
    let detectedFormat = format;

    try {
      if (format === 'auto') {
        const result = this.autoDetectFormat(trimmedInput);
        date = result.date;
        detectedFormat = result.format;
      } else {
        date = this.parseByFormat(trimmedInput, format);
        detectedFormat = format;
      }

      if (!date || isNaN(date.getTime())) {
        return this.createEmptyResult(input, detectedFormat);
      }

      return this.createDateTimeObject(input, detectedFormat, date);
    } catch (error) {
      return this.createEmptyResult(input, detectedFormat);
    }
  }

  private autoDetectFormat(input: string): { date: Date | null; format: DateInputFormat } {
    // Try negative Unix timestamp (seconds) - pre-1970 dates
    if (/^-\d+$/.test(input)) {
      const date = new Date(parseInt(input) * 1000);
      if (!isNaN(date.getTime())) {
        return { date, format: 'unix-timestamp' };
      }
    }

    // Try Unix timestamp (seconds) - 10-12 digits for future dates
    if (/^\d{10,12}$/.test(input)) {
      const date = new Date(parseInt(input) * 1000);
      if (!isNaN(date.getTime())) {
        return { date, format: 'unix-timestamp' };
      }
    }

    // Try Unix timestamp (milliseconds)
    if (/^\d{13}$/.test(input)) {
      const date = new Date(parseInt(input));
      if (!isNaN(date.getTime())) {
        return { date, format: 'unix-timestamp-ms' };
      }
    }

    // Try ISO 8601
    const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?([+-]\d{2}:\d{2})?$/;
    if (iso8601Regex.test(input)) {
      const date = new Date(input);
      if (!isNaN(date.getTime())) {
        return { date, format: 'iso8601' };
      }
    }

    // Handle ambiguous DD-MM vs MM-DD formats by checking if first part > 12
    const dashMatch = input.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) {
      const part1 = parseInt(dashMatch[1]);
      const part2 = parseInt(dashMatch[2]);

      // If first part > 12, it must be DD-MM-YYYY
      if (part1 > 12) {
        const date = new Date(Date.UTC(parseInt(dashMatch[3]), part2 - 1, part1));
        if (!isNaN(date.getTime())) {
          return { date, format: 'dd-mm-yyyy' };
        }
      }
      // If second part > 12, it must be MM-DD-YYYY
      else if (part2 > 12) {
        const date = new Date(Date.UTC(parseInt(dashMatch[3]), part1 - 1, part2));
        if (!isNaN(date.getTime())) {
          return { date, format: 'mm-dd-yyyy' };
        }
      }
      // Ambiguous - default to MM-DD-YYYY (US format)
      else {
        const date = new Date(Date.UTC(parseInt(dashMatch[3]), part1 - 1, part2));
        if (!isNaN(date.getTime())) {
          return { date, format: 'mm-dd-yyyy' };
        }
      }
    }

    // Handle ambiguous DD/MM vs MM/DD formats
    const slashMatch = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) {
      const part1 = parseInt(slashMatch[1]);
      const part2 = parseInt(slashMatch[2]);

      // If first part > 12, it must be DD/MM/YYYY
      if (part1 > 12) {
        const date = new Date(Date.UTC(parseInt(slashMatch[3]), part2 - 1, part1));
        if (!isNaN(date.getTime())) {
          return { date, format: 'dd/mm/yyyy' };
        }
      }
      // If second part > 12, it must be MM/DD/YYYY
      else if (part2 > 12) {
        const date = new Date(Date.UTC(parseInt(slashMatch[3]), part1 - 1, part2));
        if (!isNaN(date.getTime())) {
          return { date, format: 'mm/dd/yyyy' };
        }
      }
      // Ambiguous - default to MM/DD/YYYY (US format)
      else {
        const date = new Date(Date.UTC(parseInt(slashMatch[3]), part1 - 1, part2));
        if (!isNaN(date.getTime())) {
          return { date, format: 'mm/dd/yyyy' };
        }
      }
    }

    // Try other unambiguous formats
    const formats: Array<{ regex: RegExp; format: DateInputFormat; parser: (match: RegExpMatchArray) => Date }> = [
      {
        regex: /^(\d{4})-(\d{2})-(\d{2})$/,
        format: 'yyyy-mm-dd',
        parser: (match) => new Date(Date.UTC(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3])))
      },
      {
        regex: /^(\d{4})\/(\d{2})\/(\d{2})$/,
        format: 'yyyy/mm/dd',
        parser: (match) => new Date(Date.UTC(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3])))
      }
    ];

    for (const { regex, format, parser } of formats) {
      const match = input.match(regex);
      if (match) {
        const date = parser(match);
        if (!isNaN(date.getTime())) {
          return { date, format };
        }
      }
    }

    return { date: null, format: 'auto' };
  }

  private parseByFormat(input: string, format: DateInputFormat): Date | null {
    switch (format) {
      case 'unix-timestamp':
        const timestamp = parseInt(input);
        if (isNaN(timestamp)) return null;
        return new Date(timestamp * 1000);

      case 'unix-timestamp-ms':
        const timestampMs = parseInt(input);
        if (isNaN(timestampMs)) return null;
        return new Date(timestampMs);

      case 'iso8601':
        return new Date(input);

      case 'mm-dd-yyyy':
        const match1 = input.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (!match1) return null;
        return new Date(Date.UTC(parseInt(match1[3]), parseInt(match1[1]) - 1, parseInt(match1[2])));

      case 'dd-mm-yyyy':
        const match2 = input.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (!match2) return null;
        return new Date(Date.UTC(parseInt(match2[3]), parseInt(match2[2]) - 1, parseInt(match2[1])));

      case 'yyyy-mm-dd':
        const match3 = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match3) return null;
        return new Date(Date.UTC(parseInt(match3[1]), parseInt(match3[2]) - 1, parseInt(match3[3])));

      case 'mm/dd/yyyy':
        const match4 = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match4) return null;
        return new Date(Date.UTC(parseInt(match4[3]), parseInt(match4[1]) - 1, parseInt(match4[2])));

      case 'dd/mm/yyyy':
        const match5 = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match5) return null;
        return new Date(Date.UTC(parseInt(match5[3]), parseInt(match5[2]) - 1, parseInt(match5[1])));

      case 'yyyy/mm/dd':
        const match6 = input.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
        if (!match6) return null;
        return new Date(Date.UTC(parseInt(match6[1]), parseInt(match6[2]) - 1, parseInt(match6[3])));

      default:
        return null;
    }
  }

  private createDateTimeObject(rawValue: string, format: DateInputFormat, date: Date): DateTimeObject {
    return {
      rawValue,
      format,
      unixTimestamp: Math.floor(date.getTime() / 1000),
      iso8601: date.toISOString(),
      humanReadableLocal: this.formatDate(date, this.options.timezone),
      humanReadableUTC: this.formatDate(date, 'UTC'),
      timezone: this.options.timezone,
      isValid: true
    };
  }

  private createEmptyResult(rawValue: string, format: DateInputFormat): DateTimeObject {
    return {
      rawValue,
      format,
      unixTimestamp: null,
      iso8601: null,
      humanReadableLocal: null,
      humanReadableUTC: null,
      timezone: this.options.timezone,
      isValid: false
    };
  }

  private formatDate(date: Date, timezone: string): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      hour12: false
    };

    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  static getAvailableFormats(): Array<{ value: DateInputFormat; label: string; description: string }> {
    return [
      { value: 'auto', label: 'Auto Detect', description: 'Automatically detect the input format' },
      { value: 'unix-timestamp', label: 'Unix Timestamp', description: 'Seconds since epoch (10 digits)' },
      { value: 'unix-timestamp-ms', label: 'Unix Timestamp (ms)', description: 'Milliseconds since epoch (13 digits)' },
      { value: 'iso8601', label: 'ISO 8601', description: '2023-03-15T10:30:00Z' },
      { value: 'mm-dd-yyyy', label: 'MM-DD-YYYY', description: '03-15-2023' },
      { value: 'dd-mm-yyyy', label: 'DD-MM-YYYY', description: '15-03-2023' },
      { value: 'yyyy-mm-dd', label: 'YYYY-MM-DD', description: '2023-03-15' },
      { value: 'mm/dd/yyyy', label: 'MM/DD/YYYY', description: '03/15/2023' },
      { value: 'dd/mm/yyyy', label: 'DD/MM/YYYY', description: '15/03/2023' },
      { value: 'yyyy/mm/dd', label: 'YYYY/MM/DD', description: '2023/03/15' }
    ];
  }

  static getAvailableTimezones(): Array<{ value: string; label: string }> {
    const timezones = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Asia/Tokyo',
      'Asia/Shanghai',
      'Australia/Sydney'
    ];

    return timezones.map(tz => ({
      value: tz,
      label: tz.replace('_', ' ')
    }));
  }

  static getCurrentTimestamps(): Record<string, string> {
    const now = new Date();
    
    return {
      'Unix Timestamp (seconds)': Math.floor(now.getTime() / 1000).toString(),
      'Unix Timestamp (milliseconds)': now.getTime().toString(),
      'ISO 8601': now.toISOString(),
      'Local Time': now.toLocaleString(),
      'UTC Time': now.toUTCString()
    };
  }
}